import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import LessonCanvas from '../components/LessonCanvas';
import { startLesson } from '../canvasEngine';
import { knowledgeGraphToLessonSpec } from '../canvasEngine/knowledgeGraphToLessonSpec';
import { buildCheckpointOptions } from '../canvasEngine/core/checkpointUtils';
import LearningContextPreview from '../components/ui/LearningContextPreview';
import { selectActiveCourse, selectActiveLesson } from '../redux/classroomSlice';
import { selectProfile } from '../redux/profileSlice';
import { AnimatedLessonService } from '../services/llmClient/AnimatedLessonService';
import eventStreamService from '../services/supabaseClient/eventStreamService';
import { useAgentModal } from '../context/AgentModalContext';
import './LessonDemo.css';

const normalizeObjectives = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

// Hook to handle canvas DPR and resize
function useResizeCanvas(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;

      // Get parent dimensions (the actual available space)
      const parent = canvas.parentElement;
      if (!parent) return;

      const parentRect = parent.getBoundingClientRect();
      const parentW = parentRect.width;
      const parentH = parentRect.height;

      if (!parentW || !parentH) return;

      // Fill-fit: maximize drawable area across phone/tablet/desktop instead of locking to a wide ratio.
      const cssW = parentW;
      const cssH = parentH;

      // Set CSS dimensions to contained size
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;

      // Set internal resolution with DPR
      const nextW = Math.round(cssW * dpr);
      const nextH = Math.round(cssH * dpr);

      if (canvas.width !== nextW) canvas.width = nextW;
      if (canvas.height !== nextH) canvas.height = nextH;

      const ctx = canvas.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement || canvas);

    // First paint
    resize();

    // Optional: orientation change
    const onOrient = () => setTimeout(resize, 200);
    window.addEventListener('orientationchange', onOrient);

    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', onOrient);
    };
  }, [canvasRef]);
}

const LessonDemo = ({
  forcedPresentationMode = null,
  standalone = false,
  initialTopic = '',
  staticSceneData = null,
  staticSceneRequest = null,
  topicEditable = true,
  demoTopicOptions = [],
  selectedDemoTopicKey = '',
  onDemoTopicChange = null,
}) => {
  const location = useLocation();
  const profile = useSelector(selectProfile);
  const activeCourse = useSelector(selectActiveCourse);
  const activeLesson = useSelector(selectActiveLesson);
  const { openAgentModal } = useAgentModal();

  const routeState = location?.state || {};
  const isSceneMode = forcedPresentationMode === 'scene' || routeState.presentationMode === 'scene';
  const showDebugOverlay =
    (typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('debugCanvas') === '1') ||
    routeState.debugCanvas === true;

  const defaultTopic = standalone
    ? initialTopic || routeState.unitTitle || 'Dynamic Programming'
    : routeState.unitTitle ||
      activeLesson?.title ||
      activeLesson?.unit_objective ||
      activeCourse?.title ||
      initialTopic ||
      'Dynamic Programming';
  const normalizedCourseObjectives = normalizeObjectives(activeCourse?.objectives);
  const learningContextCourseTitle =
    routeState.courseTitle || activeCourse?.title || 'Untitled Course';
  const learningContextObjective =
    routeState.unitObjective || activeLesson?.objective || activeLesson?.unit_objective || '';
  const [topic, setTopic] = useState(defaultTopic);
  const [generationStatus, setGenerationStatus] = useState('idle');
  const [generationError, setGenerationError] = useState('');
  const [lessonSpec, setLessonSpec] = useState(null);
  const [speed, setSpeed] = useState(1.0);
  const [isPaused, setIsPaused] = useState(false);
  const [engineDebug, setEngineDebug] = useState(null);
  const [checkpointOptions, setCheckpointOptions] = useState([]);
  const [selectedCheckpointId, setSelectedCheckpointId] = useState('');
  const canvasRef = useRef(null);
  const handleRef = useRef(null);

  // Handle canvas resize with DPR
  useResizeCanvas(canvasRef);

  const generateLesson = useCallback(async () => {
    setGenerationStatus('loading');
    setGenerationError('');

    try {
      const sceneStreamKey = `scene:${routeState.unitId || activeLesson?.id || topic}`;
      void eventStreamService.emitEvent({
        userId: profile?.id || null,
        sessionId: sceneStreamKey,
        streamKey: sceneStreamKey,
        eventFamily: 'scene',
        eventType: 'scene_generation_started',
        eventName: 'scene_generation_started',
        origin: 'lesson_demo',
        actorType: 'user',
        actorId: profile?.id || null,
        entityType: 'scene_lesson',
        entityId: topic,
        courseId: routeState.courseId || activeCourse?.id || null,
        unitId: routeState.unitId || activeLesson?.id || null,
        lessonId: routeState.unitId || activeLesson?.id || null,
        payload: {
          topic,
          presentationMode: isSceneMode ? 'scene' : 'visual',
        },
      });

      const courseId = routeState.courseId || activeCourse?.id || '';
      const unitId = routeState.unitId || activeLesson?.id || '';
      const lessonObjective =
        routeState.unitObjective || activeLesson?.objective || activeLesson?.unit_objective || '';
      const presentationMode = isSceneMode ? 'scene' : 'visual';
      const contentCacheKey = AnimatedLessonService.buildLessonContentCacheKey({
        courseId,
        unitId,
        topic,
        lessonObjective,
        presentationMode,
      });
      const cachedLessonContent = AnimatedLessonService.getCachedLessonContent(contentCacheKey);

      const payload = {
        topic,
        courseTitle: activeCourse?.title || '',
        lessonObjective,
        learner: {
          name: profile?.name || '',
          educationLevel: profile?.educationLevel || '',
          interests: profile?.interests || '',
          careerGoals: profile?.careerGoals || '',
        },
        animation: {
          mode: 'chalkboard',
          requireLevelOrder: true,
          includePrerequisiteEdges: true,
        },
      };

      const loadKnowledgeGraphLesson = async () => {
        const cacheKey = AnimatedLessonService.buildKnowledgeGraphCacheKey({
          courseId,
          unitId,
          topic,
        });
        const cachedGraph = AnimatedLessonService.getCachedKnowledgeGraph(cacheKey);
        const graphResponse =
          cachedGraph || (await AnimatedLessonService.generateKnowledgeGraphLesson(payload));

        if (!cachedGraph) {
          AnimatedLessonService.saveInitialKnowledgeGraph(cacheKey, graphResponse);
        }

        const detailPayload = {
          lessonId: graphResponse?.lessonId || '',
          courseTitle: payload.courseTitle,
          lessonObjective: payload.lessonObjective,
          learner: payload.learner,
          animation: payload.animation,
          knowledgeGraph: Array.isArray(graphResponse?.knowledgeGraph)
            ? graphResponse.knowledgeGraph
            : [],
        };
        const topicDetails =
          await AnimatedLessonService.generateSlideContentsFromGraph(detailPayload);

        return {
          ...graphResponse,
          knowledgeGraph: (graphResponse?.knowledgeGraph || []).map((topicNode, index) => ({
            ...topicNode,
            detail: topicDetails[index] || null,
          })),
        };
      };

      let enrichedResponse = null;
      if (cachedLessonContent?.knowledgeGraph?.length) {
        enrichedResponse = cachedLessonContent;
      } else if (isSceneMode) {
        if (staticSceneData?.treeSlides && staticSceneData?.nodeContentById) {
          const detailsById = new Map(Object.entries(staticSceneData.nodeContentById));
          enrichedResponse = {
            lessonId: staticSceneData.lessonId || `scene_demo_${Date.now()}`,
            title: staticSceneData.title || `${topic} Scene Demo`,
            knowledgeGraph: AnimatedLessonService.buildKnowledgeGraphFromSceneTree(
              staticSceneData.treeSlides,
              detailsById
            ),
          };
        } else if (staticSceneRequest && typeof staticSceneRequest === 'object') {
          const initialNodeCount =
            typeof window !== 'undefined' && window.innerWidth <= 768 ? 1 : 2;
          enrichedResponse = await AnimatedLessonService.generateSceneSlidesFromTree({
            topic: staticSceneRequest.topic || topic,
            lessonId: '',
            courseTitle: staticSceneRequest.courseTitle || payload.courseTitle,
            lessonObjective: staticSceneRequest.lessonObjective || payload.lessonObjective,
            learner: staticSceneRequest.learner || payload.learner,
            animation: staticSceneRequest.animation || payload.animation,
            contentCacheKey,
            initialNodeCount,
          });
        } else {
          const initialNodeCount =
            typeof window !== 'undefined' && window.innerWidth <= 768 ? 1 : 2;
          try {
            enrichedResponse = await AnimatedLessonService.generateSceneSlidesFromTree({
              topic,
              lessonId: '',
              courseTitle: payload.courseTitle,
              lessonObjective: payload.lessonObjective,
              learner: payload.learner,
              animation: payload.animation,
              contentCacheKey,
              initialNodeCount,
            });
          } catch (sceneError) {
            if (typeof window !== 'undefined' && window?.console?.warn) {
              window.console.warn(
                '[LessonDemo] Scene tree generation failed, falling back to knowledge graph lesson.',
                {
                  topic,
                  error: sceneError?.message || String(sceneError),
                }
              );
            }
            enrichedResponse = await loadKnowledgeGraphLesson();
          }
        }
        AnimatedLessonService.saveLessonContent(contentCacheKey, enrichedResponse);
      } else {
        enrichedResponse = await loadKnowledgeGraphLesson();
        AnimatedLessonService.saveLessonContent(contentCacheKey, enrichedResponse);
      }

      const generatedSpec = knowledgeGraphToLessonSpec(enrichedResponse, {
        title: enrichedResponse?.title || topic,
        presentationMode: isSceneMode ? 'scene' : 'visual',
      });

      setLessonSpec(generatedSpec);
      setCheckpointOptions([]);
      setSelectedCheckpointId('');
      setGenerationStatus('ready');
      void eventStreamService.emitEvent({
        userId: profile?.id || null,
        sessionId: sceneStreamKey,
        streamKey: sceneStreamKey,
        eventFamily: isSceneMode ? 'scene' : 'visual',
        eventType: 'lesson_generation_succeeded',
        eventName: 'lesson_generation_succeeded',
        origin: 'lesson_demo',
        actorType: 'system',
        actorId: 'lesson_demo',
        entityType: 'scene_lesson',
        entityId: topic,
        courseId: courseId || null,
        unitId: unitId || null,
        lessonId: unitId || null,
        success: true,
        payload: {
          topic,
          nodeCount: Array.isArray(enrichedResponse?.knowledgeGraph)
            ? enrichedResponse.knowledgeGraph.length
            : 0,
        },
      });
    } catch (error) {
      setGenerationStatus('error');
      setGenerationError(error.message || 'Unable to load lesson right now.');
      void eventStreamService.emitEvent({
        userId: profile?.id || null,
        sessionId: `scene:${routeState.unitId || activeLesson?.id || topic}`,
        streamKey: `scene:${routeState.unitId || activeLesson?.id || topic}`,
        eventFamily: isSceneMode ? 'scene' : 'visual',
        eventType: 'lesson_generation_failed',
        eventName: 'lesson_generation_failed',
        origin: 'lesson_demo',
        actorType: 'system',
        actorId: 'lesson_demo',
        entityType: 'scene_lesson',
        entityId: topic,
        courseId: routeState.courseId || activeCourse?.id || null,
        unitId: routeState.unitId || activeLesson?.id || null,
        lessonId: routeState.unitId || activeLesson?.id || null,
        success: false,
        errorCode: error?.message || 'lesson_generation_failed',
        payload: {
          topic,
        },
      });
    }
  }, [
    activeCourse,
    activeLesson,
    isSceneMode,
    staticSceneRequest,
    staticSceneData,
    profile,
    routeState.courseId,
    routeState.unitId,
    routeState.unitObjective,
    topic,
  ]);

  useEffect(() => {
    setTopic(defaultTopic);
  }, [defaultTopic]);

  useEffect(() => {
    generateLesson();
  }, [generateLesson]);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }
    if (!lessonSpec) {
      return;
    }

    try {
      handleRef.current = startLesson(canvasRef.current, lessonSpec);
      handleRef.current.setSpeed(speed);
      setIsPaused(false);
      setCheckpointOptions([]);
      setSelectedCheckpointId('');
      setEngineDebug(
        typeof handleRef.current.getDebugSnapshot === 'function'
          ? handleRef.current.getDebugSnapshot()
          : null
      );
    } catch (error) {
      setGenerationStatus('error');
      setGenerationError(error?.message || 'Failed to start lesson.');
    }

    return () => {
      if (handleRef.current) {
        handleRef.current.destroy();
        handleRef.current = null;
        setEngineDebug(null);
      }
    };
  }, [lessonSpec, speed]);

  useEffect(() => {
    if (!handleRef.current || typeof handleRef.current.getDebugSnapshot !== 'function')
      return undefined;
    const intervalId = setInterval(() => {
      if (!handleRef.current || typeof handleRef.current.getDebugSnapshot !== 'function') return;
      setEngineDebug(handleRef.current.getDebugSnapshot());
      if (typeof handleRef.current.getCheckpoints === 'function') {
        const nextOptions = buildCheckpointOptions(handleRef.current.getCheckpoints());
        setCheckpointOptions(nextOptions);
      }
    }, 300);
    return () => clearInterval(intervalId);
  }, [lessonSpec]);

  useEffect(() => {
    if (checkpointOptions.length === 0) {
      if (selectedCheckpointId) setSelectedCheckpointId('');
      return;
    }

    const hasSelected = checkpointOptions.some((option) => option.value === selectedCheckpointId);
    if (!hasSelected) {
      setSelectedCheckpointId(checkpointOptions[0].value);
    }
  }, [checkpointOptions, selectedCheckpointId]);

  const handleSpeedChange = (newSpeed) => {
    setSpeed(newSpeed);
    if (handleRef.current) {
      handleRef.current.setSpeed(newSpeed);
    }
  };

  const handlePauseToggle = () => {
    if (!handleRef.current) return;

    if (typeof handleRef.current.togglePause === 'function') {
      const paused = handleRef.current.togglePause();
      setIsPaused(paused);
      return;
    }

    if (isPaused && typeof handleRef.current.resume === 'function') {
      handleRef.current.resume();
      setIsPaused(false);
    } else if (!isPaused && typeof handleRef.current.pause === 'function') {
      handleRef.current.pause();
      setIsPaused(true);
    }
  };

  const handleJumpToCheckpoint = () => {
    if (!selectedCheckpointId || !handleRef.current) return;
    if (typeof handleRef.current.jumpToCheckpoint !== 'function') return;
    const jumped = handleRef.current.jumpToCheckpoint(selectedCheckpointId);
    if (jumped) setIsPaused(false);
  };

  return (
    <div className="lesson-demo-page">
      <div className="lesson-demo-header">
        <h1 className="lesson-demo-title">{isSceneMode ? 'Scene Lesson' : 'Animated Lesson'}</h1>
        <div className="lesson-demo-actions">
          {Array.isArray(demoTopicOptions) && demoTopicOptions.length > 0 && (
            <select
              value={selectedDemoTopicKey || demoTopicOptions[0]?.key || ''}
              onChange={(e) => {
                if (typeof onDemoTopicChange === 'function') onDemoTopicChange(e.target.value);
              }}
              className="lesson-demo-topic-select"
              aria-label="Select demo topic"
            >
              {demoTopicOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Enter a lesson topic"
            className="lesson-demo-topic-input"
            readOnly={!topicEditable}
            disabled={!topicEditable}
          />
          {topicEditable && (
            <button
              onClick={generateLesson}
              disabled={generationStatus === 'loading'}
              className="lesson-demo-ai-start"
              aria-label={generationStatus === 'loading' ? 'Preparing lesson' : 'Start lesson'}
              title={generationStatus === 'loading' ? 'Preparing lesson' : 'Start lesson'}
            >
              {generationStatus === 'loading' ? (
                <span className="lesson-demo-ai-loading" aria-hidden="true">
                  ✦ ✧
                </span>
              ) : (
                <span className="lesson-demo-ai-icon-wrap" aria-hidden="true">
                  <span className="lesson-demo-ai-icon lesson-demo-ai-icon-a">✦</span>
                  <span className="lesson-demo-ai-icon lesson-demo-ai-icon-b">✧</span>
                  <span className="lesson-demo-ai-icon lesson-demo-ai-icon-c">✦</span>
                </span>
              )}
            </button>
          )}
          <button
            onClick={() =>
              openAgentModal({
                agentId: 'professor',
                origin: 'lesson-demo',
                contextPayload: {
                  courseTitle: activeCourse?.title || '',
                  lessonTitle: topic,
                  activityLeader: 'Professor',
                },
              })
            }
            className="lesson-demo-prof-btn"
            aria-label="Open professor assistant"
            title="Professor assistant"
          >
            <i className="fa-solid fa-robot" aria-hidden="true" />
          </button>
        </div>
      </div>
      {(generationStatus === 'error' || generationStatus === 'loading' || !lessonSpec) && (
        <div className={`lesson-demo-status lesson-demo-status--${generationStatus || 'idle'}`}>
          {generationStatus === 'loading' && 'Preparing your learning context...'}
          {generationStatus === 'error' && `Unable to load lesson: ${generationError}`}
          {generationStatus !== 'loading' &&
            generationStatus !== 'error' &&
            !lessonSpec &&
            'Loading lesson content...'}
        </div>
      )}
      <div className="lesson-demo-stage">
        <div
          className="lesson-root"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        >
          {(generationStatus === 'loading' || (!lessonSpec && generationStatus !== 'error')) && (
            <div className="lesson-demo-context-shell">
              <LearningContextPreview
                courseTitle={learningContextCourseTitle}
                lessonTitle={topic}
                lessonObjective={learningContextObjective || 'No unit objective provided.'}
                courseObjectives={normalizedCourseObjectives}
                activityLabel={isSceneMode ? 'Scene' : 'Visual'}
                eyebrow={
                  isSceneMode ? 'Preparing your scene lesson' : 'Preparing your visual lesson'
                }
                subtitle={
                  isSceneMode
                    ? "We're grounding this scene lesson in the current course, unit, and objective before the canvas starts."
                    : "We're grounding this visual lesson in the current course, unit, and objective before the animation starts."
                }
                loadingMessage={
                  isSceneMode ? 'Building your scene lesson...' : 'Building your visual lesson...'
                }
              />
            </div>
          )}
          <LessonCanvas ref={canvasRef} />
          {showDebugOverlay && engineDebug && (
            <div className="lesson-demo-debug">
              <div style={{ fontWeight: 700, marginBottom: '6px' }}>Phase Integrity</div>
              <div>Nodes: {engineDebug.slideOrder.length}</div>
              <div>Active: {engineDebug.activeNodeId || 'n/a'}</div>
              <div>Paused: {engineDebug.paused ? 'yes' : 'no'}</div>
              <div>Manipulate: {engineDebug.manipulateMode ? 'yes' : 'no'}</div>
              <div>Guides: {engineDebug.regionGuides ? 'yes' : 'no'}</div>
              <div>Zooming: {engineDebug.zooming ? 'yes' : 'no'}</div>
              <div>Transitioning: {engineDebug.transitioning ? 'yes' : 'no'}</div>
              <div>Heap nextIndex: {engineDebug.topology?.nextInsertIndex ?? 'n/a'}</div>
              <div>RenderProgram packets: {engineDebug.renderProgramPackets ?? 0}</div>
              <div>Hover slot: {engineDebug.interaction?.hoverSlot ?? 'none'}</div>
              <div>Selected child: {engineDebug.interaction?.selectedChildId || 'none'}</div>
              <div>Zoom targets: {engineDebug.zoomTargets?.length ?? 0}</div>
              <div>Contract issues: {engineDebug.pedagogicalContractDiagnostics?.length ?? 0}</div>
              <div style={{ marginTop: '6px', opacity: 0.9 }}>
                SlideOrder: {engineDebug.slideOrder.slice(0, 8).join(' -> ')}
                {engineDebug.slideOrder.length > 8 ? ' ...' : ''}
              </div>
              {Array.isArray(engineDebug.pedagogicalContractDiagnostics) &&
                engineDebug.pedagogicalContractDiagnostics.length > 0 && (
                  <div style={{ marginTop: '6px', opacity: 0.92 }}>
                    {engineDebug.pedagogicalContractDiagnostics.slice(0, 4).map((item, idx) => (
                      <div key={`${item.code}_${idx}`}>
                        [{item.severity}] {item.nodeId || 'unknown'}: {item.code}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          )}
          <div className="lesson-demo-controls">
            <button
              onClick={handlePauseToggle}
              className={`lesson-demo-control-btn lesson-demo-control-btn--pause ${
                isPaused ? 'is-active' : ''
              }`}
            >
              {isPaused ? 'Play' : 'Pause'}
            </button>
            <button
              onClick={() => handleSpeedChange(0.5)}
              className={`lesson-demo-control-btn lesson-demo-control-btn--speed ${
                speed === 0.5 ? 'is-active' : ''
              }`}
            >
              0.5x
            </button>
            <button
              onClick={() => handleSpeedChange(1.0)}
              className={`lesson-demo-control-btn lesson-demo-control-btn--speed ${
                speed === 1.0 ? 'is-active' : ''
              }`}
            >
              1x
            </button>
            <button
              onClick={() => handleSpeedChange(2.0)}
              className={`lesson-demo-control-btn lesson-demo-control-btn--speed ${
                speed === 2.0 ? 'is-active' : ''
              }`}
            >
              2x
            </button>
            <select
              className="lesson-demo-control-select"
              value={selectedCheckpointId}
              onChange={(event) => setSelectedCheckpointId(event.target.value)}
              disabled={checkpointOptions.length === 0}
              aria-label="Scene moment selector"
              title={
                checkpointOptions.length === 0
                  ? 'No scene moments available yet'
                  : 'Choose a scene moment'
              }
            >
              {checkpointOptions.length === 0 ? (
                <option value="">No scene moments yet</option>
              ) : (
                checkpointOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))
              )}
            </select>
            <button
              onClick={handleJumpToCheckpoint}
              className="lesson-demo-control-btn"
              disabled={!selectedCheckpointId || checkpointOptions.length === 0}
              aria-label="Go to selected scene moment"
              title="Go to selected scene moment"
            >
              Go
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonDemo;
